<!--
 * @Description: 充电枪白名单 新增/编辑 表单（内嵌于抽屉，枪粒度）
 * 新增：左侧电站列表(可搜索) → 点击电站加载右侧充电枪列表(可搜索、可全选) → 跨站累计多选 → 保存多条
-->
<template>
  <div class="gun-whitelist-form">
    <ykc-form ref="form" :model="form" :rules="rules" label-width="120px">
      <!-- 新增：双栏电站→充电枪穿梭选择 -->
      <template v-if="isAdd">
        <ykc-form-item label="选择添加电桩" prop="gunIds">
          <div class="transfer">
            <!-- 左侧：电站 -->
            <div class="transfer-pane">
              <div class="transfer-pane__title">电站</div>
              <div class="transfer-pane__search">
                <ykc-input
                  v-model="stationKeyword"
                  placeholder="搜索电站名称"
                  clearable
                  @keyup.enter.native="loadStations"
                  @blur="loadStations"></ykc-input>
              </div>
              <div class="transfer-pane__body" v-loading="stationLoading">
                <div
                  v-for="station in stationList"
                  :key="station.stationId"
                  :class="[
                    'transfer-item',
                    { 'transfer-item--active': station.stationId === activeStationId },
                  ]"
                  @click="handleStationClick(station)">
                  <span class="transfer-item__text" :title="station.stationName">
                    {{ station.stationName }}
                  </span>
                  <span
                    v-if="stationSelectedCount(station.stationId) > 0"
                    class="transfer-item__badge">
                    {{ stationSelectedCount(station.stationId) }}
                  </span>
                </div>
                <div v-if="!stationLoading && stationList.length === 0" class="transfer-empty">
                  暂无电站
                </div>
              </div>
            </div>

            <!-- 右侧：充电枪 -->
            <div class="transfer-pane">
              <div class="transfer-pane__title">
                电桩
                <el-checkbox
                  v-if="gunList.length > 0"
                  class="transfer-pane__all"
                  :value="isAllSelected"
                  :indeterminate="isIndeterminate"
                  @change="toggleSelectAll">
                  全选
                </el-checkbox>
              </div>
              <div class="transfer-pane__search">
                <ykc-input
                  v-model="gunKeyword"
                  placeholder="搜索电桩编码"
                  clearable
                  :disabled="!activeStationId"
                  @keyup.enter.native="loadGuns"
                  @blur="loadGuns"></ykc-input>
              </div>
              <div class="transfer-pane__body" v-loading="gunLoading">
                <div v-for="gun in gunList" :key="gun.gunId" class="transfer-item">
                  <el-checkbox
                    :value="!!selectedMap[gun.gunId]"
                    @change="val => toggleGun(gun, val)">
                    <span class="transfer-item__text" :title="gun.gunCode">{{ gun.gunCode }}</span>
                  </el-checkbox>
                </div>
                <div v-if="!activeStationId" class="transfer-empty">请先选择左侧电站</div>
                <div v-else-if="!gunLoading && gunList.length === 0" class="transfer-empty">
                  该电站暂无电桩
                </div>
              </div>
            </div>
          </div>

          <!-- 已选充电枪 -->
          <div class="selected-box">
            <div class="selected-box__head">
              已选电桩（{{ selectedGuns.length }}）
              <el-button v-if="selectedGuns.length > 0" type="text" @click="clearSelected">
                清空
              </el-button>
            </div>
            <div class="selected-box__body">
              <el-tag
                v-for="item in selectedGuns"
                :key="item.gunId"
                closable
                size="small"
                class="selected-tag"
                @close="removeSelected(item.gunId)">
                {{ item.gunCode }}
              </el-tag>
              <span v-if="selectedGuns.length === 0" class="transfer-empty">尚未选择</span>
            </div>
          </div>
        </ykc-form-item>
      </template>

      <!-- 编辑：充电枪信息只读 -->
      <template v-else>
        <ykc-form-item label="电桩编码">
          <span>{{ form.gunCode || '-' }}</span>
        </ykc-form-item>
        <ykc-form-item label="电站名称">
          <span>{{ form.stationName || '-' }}</span>
        </ykc-form-item>
      </template>

      <ykc-form-item label="生效/失效时间" prop="dateRange">
        <el-date-picker
          v-model="form.dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="生效时间"
          end-placeholder="失效时间"
          value-format="yyyy-MM-dd"
          :picker-options="pickerOptions"
          style="width: 100%"></el-date-picker>
      </ykc-form-item>

      <ykc-form-item label="添加原因" prop="reason">
        <el-input
          v-if="isAdd"
          v-model="form.reason"
          type="textarea"
          :rows="3"
          maxlength="500"
          show-word-limit
          placeholder="请输入添加原因"></el-input>
        <span v-else>{{ form.reason || '-' }}</span>
      </ykc-form-item>
    </ykc-form>
  </div>
</template>

<script>
  import { gunWhitelistApi, barrierPage } from '@/service/apis';

  export default {
    name: 'GunWhitelistAddOrEdit',
    props: {
      drawerType: {
        type: String,
        default: 'add',
      },
      editData: {
        type: Object,
        default: null,
      },
    },
    data() {
      const isAdd = this.drawerType !== 'edit';
      const validateGunIds = (rule, value, callback) => {
        if (!this.selectedGunIds || this.selectedGunIds.length === 0) {
          callback(new Error('请选择电桩'));
        } else {
          callback();
        }
      };
      const validateDateRange = (rule, value, callback) => {
        if (!value || value.length !== 2 || !value[0] || !value[1]) {
          callback(new Error('请选择生效时间与失效时间'));
          return;
        }
        if (new Date(value[1]).getTime() < new Date(value[0]).getTime()) {
          callback(new Error('失效时间必须大于生效时间'));
          return;
        }
        callback();
      };
      return {
        submitting: false,
        // 左侧电站
        stationLoading: false,
        stationKeyword: '',
        stationList: [],
        activeStationId: null,
        activeStationName: '',
        // 右侧充电枪
        gunLoading: false,
        gunKeyword: '',
        gunList: [],
        // 已选：gunId -> { gunId, gunCode, stationId, stationName }
        selectedMap: {},
        form: {
          dateRange: [],
          reason: '',
          // 编辑态只读展示
          id: null,
          gunCode: '',
          stationName: '',
        },
        pickerOptions: isAdd
          ? {
              disabledDate(time) {
                // 禁选过去（早于今天0点）
                return time.getTime() < Date.now() - 24 * 60 * 60 * 1000;
              },
            }
          : {},
        rules: {
          gunIds: [{ required: true, validator: validateGunIds, trigger: 'change' }],
          dateRange: [{ required: true, validator: validateDateRange, trigger: 'change' }],
          reason: isAdd ? [{ required: true, message: '请输入添加原因', trigger: 'blur' }] : [],
        },
      };
    },
    computed: {
      isAdd() {
        return this.drawerType !== 'edit';
      },
      selectedGuns() {
        return Object.values(this.selectedMap);
      },
      selectedGunIds() {
        return Object.keys(this.selectedMap).map(id => Number(id));
      },
      isAllSelected() {
        return this.gunList.length > 0 && this.gunList.every(g => !!this.selectedMap[g.gunId]);
      },
      isIndeterminate() {
        const selectedInList = this.gunList.filter(g => !!this.selectedMap[g.gunId]).length;
        return selectedInList > 0 && selectedInList < this.gunList.length;
      },
    },
    created() {
      if (this.isAdd) {
        this.loadStations();
      } else {
        this.initEditForm();
      }
    },
    methods: {
      // 加载电站列表（仅淘淘充平台）
      loadStations() {
        this.stationLoading = true;
        barrierPage
          .findStationList({})
          .then(res => {
            const list = res || [];
            if (this.stationKeyword) {
              this.stationList = list.filter(
                s => s.stationName && s.stationName.indexOf(this.stationKeyword) !== -1
              );
            } else {
              this.stationList = list;
            }
          })
          .catch(err => {
            console.error('获取电站列表失败:', err);
            this.stationList = [];
          })
          .finally(() => {
            this.stationLoading = false;
          });
      },
      // 点击电站，加载右侧充电枪
      handleStationClick(station) {
        this.activeStationId = station.stationId;
        this.activeStationName = station.stationName;
        this.gunKeyword = '';
        this.loadGuns();
      },
      // 加载当前电站的充电枪
      loadGuns() {
        if (!this.activeStationId) return;
        this.gunLoading = true;
        gunWhitelistApi
          .getGunsByStation({
            stationId: this.activeStationId,
            gunCode: this.gunKeyword || '',
          })
          .then(res => {
            this.gunList = res || [];
          })
          .catch(err => {
            console.error('获取充电枪失败:', err);
            this.gunList = [];
          })
          .finally(() => {
            this.gunLoading = false;
          });
      },
      // 单枪勾选/取消
      toggleGun(gun, checked) {
        if (checked) {
          this.$set(this.selectedMap, gun.gunId, {
            gunId: gun.gunId,
            gunCode: gun.gunCode,
            stationId: this.activeStationId,
            stationName: this.activeStationName,
          });
        } else {
          this.$delete(this.selectedMap, gun.gunId);
        }
        this.validateGunField();
      },
      // 全选/取消当前列表
      toggleSelectAll(checked) {
        this.gunList.forEach(gun => {
          if (checked) {
            this.$set(this.selectedMap, gun.gunId, {
              gunId: gun.gunId,
              gunCode: gun.gunCode,
              stationId: this.activeStationId,
              stationName: this.activeStationName,
            });
          } else {
            this.$delete(this.selectedMap, gun.gunId);
          }
        });
        this.validateGunField();
      },
      // 移除已选
      removeSelected(gunId) {
        this.$delete(this.selectedMap, gunId);
        this.validateGunField();
      },
      clearSelected() {
        this.selectedMap = {};
        this.validateGunField();
      },
      // 某电站下已选数量（角标）
      stationSelectedCount(stationId) {
        return this.selectedGuns.filter(g => g.stationId === stationId).length;
      },
      validateGunField() {
        this.$refs.form && this.$refs.form.validateField('gunIds');
      },
      // 编辑态回填
      initEditForm() {
        const row = this.editData || {};
        this.form.id = row.id;
        this.form.gunCode = row.gunCode;
        this.form.stationName = row.stationName;
        this.form.reason = row.reason;
        this.form.dateRange = [this.toDate(row.effectiveTime), this.toDate(row.expireTime)];
      },
      // 取日期字符串的日期部分（yyyy-MM-dd）
      toDate(val) {
        if (!val) return '';
        return String(val).split(' ')[0];
      },
      // 父组件调用
      submit() {
        this.$refs.form.validate(valid => {
          if (!valid) return;
          if (this.submitting) return;
          this.submitting = true;
          const effectiveTime = `${this.form.dateRange[0]} 00:00:00`;
          const expireTime = `${this.form.dateRange[1]} 23:59:59`;
          const request = this.isAdd
            ? gunWhitelistApi.add({
                gunIds: this.selectedGunIds,
                effectiveTime,
                expireTime,
                reason: this.form.reason,
              })
            : gunWhitelistApi.edit({
                id: this.form.id,
                effectiveTime,
                expireTime,
              });
          request
            .then(() => {
              this.$message.success(this.isAdd ? '添加成功' : '编辑成功');
              this.$emit('success');
            })
            .catch(err => {
              console.error('保存失败:', err);
              this.$message.error(err?.message || '保存失败');
            })
            .finally(() => {
              this.submitting = false;
            });
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .gun-whitelist-form {
    padding: 20px;

    .transfer {
      display: flex;
      gap: 12px;
      width: 100%;

      &-pane {
        flex: 1;
        min-width: 0;
        border: 1px solid #e2e8f0;
        border-radius: 6px;
        display: flex;
        flex-direction: column;
        height: 320px;

        &__title {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 12px;
          font-size: 13px;
          font-weight: 600;
          color: #1e293b;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        &__all {
          font-weight: normal;
        }

        &__search {
          padding: 8px;
          border-bottom: 1px solid #f1f5f9;
        }

        &__body {
          flex: 1;
          overflow-y: auto;
          padding: 4px 0;
        }
      }
    }

    .transfer-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 6px 12px;
      cursor: pointer;
      font-size: 13px;

      &:hover {
        background: #f1f5f9;
      }

      &--active {
        background: #e0f2fe;
      }

      &__text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: inline-block;
        max-width: 150px;
      }

      &__badge {
        min-width: 18px;
        height: 18px;
        line-height: 18px;
        text-align: center;
        padding: 0 5px;
        border-radius: 9px;
        font-size: 12px;
        background: #3b82f6;
        color: #fff;
      }
    }

    .transfer-empty {
      padding: 16px 12px;
      text-align: center;
      color: #94a3b8;
      font-size: 12px;
    }

    .selected-box {
      margin-top: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 6px;

      &__head {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 12px;
        font-size: 13px;
        color: #1e293b;
        border-bottom: 1px solid #f1f5f9;
        background: #f8fafc;
      }

      &__body {
        padding: 8px 12px;
        min-height: 40px;
        max-height: 120px;
        overflow-y: auto;
      }
    }

    .selected-tag {
      margin: 0 6px 6px 0;
    }
  }
</style>
